import { create, show, useModal } from "@ebay/nice-modal-react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import Select from "ds/components/Select";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";

import { GET_ACCOUNT_DELETION_DATA } from "../useAccountDeletion/gql";
import { useToggleAccountDeletionMark } from "../useAccountDeletion/mutations";
import { DELETION_REASONS } from "../AccountDeletionCard/constants";
import { trackAccountMarkedForDeletion } from "../AccountDeletionCard/analytics";

type FormData = {
  reason: string;
  additionalFeedback: string;
};

const MarkForDeletionModal = create(function MarkForDeletionModal() {
  const modal = useModal();

  const [toggleMark] = useToggleAccountDeletionMark({
    refetchQueries: [{ query: GET_ACCOUNT_DELETION_DATA }],
  });

  const form = useForm<FormData>({
    defaultValues: {
      reason: "",
      additionalFeedback: "",
    },
    mode: "onChange",
  });

  const { control, handleSubmit, watch, formState } = form;
  const selectedReason = watch("reason");
  const isOtherSelected = selectedReason === "other";

  const onSubmit = async (data: FormData) => {
    await toggleMark({ reason: data.reason, description: data.additionalFeedback }, (result) => {
      if (result?.accountToggleDeletionMark) {
        // Track analytics event
        trackAccountMarkedForDeletion({
          reason: DELETION_REASONS.find((r) => r.value === data.reason)?.label || data.reason,
          additionalFeedback: data.additionalFeedback,
        });

        modal.hide();
      }
    });
  };

  return (
    <ModalSimple>
      <ModalHeader title="Mark for deletion" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <ModalContent gap="x-large">
            <Typography variant="p-body3" tag="p">
              Run creation will be disabled immediately. After <strong>7 days</strong>, you'll be
              able to permanently delete this account, including all user accounts and resources.
              You can cancel this process at any time.
              <br />
              <br />
              Please let us know why you're marking this account for deletion — your feedback helps
              us improve.
            </Typography>

            <Controller
              name="reason"
              control={control}
              rules={{ required: "Please select a reason" }}
              render={({ field, fieldState }) => (
                <FormField
                  label="Reason for deleting your account"
                  isOptional={false}
                  error={fieldState.error?.message}
                  noMargin
                >
                  {({ ariaInputProps }) => (
                    <Select
                      value={field.value}
                      items={DELETION_REASONS}
                      onChange={field.onChange}
                      placeholder="Select reason"
                      isError={!!fieldState.error}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="additionalFeedback"
              control={control}
              rules={{
                required: isOtherSelected ? "Please provide additional details" : false,
              }}
              render={({ field, fieldState }) => (
                <FormField
                  label="Anything else you'd like to share"
                  isOptional={!isOtherSelected}
                  error={fieldState.error?.message}
                  noMargin
                >
                  {({ ariaInputProps }) => (
                    <Textarea
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Enter your feedback..."
                      minRows={4}
                      maxRows={10}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />
          </ModalContent>

          <ModalFooter>
            <ModalCancelButton onClick={modal.hide} />
            <Button
              variant="dangerPrimary"
              type="submit"
              loading={formState.isSubmitting}
              disabled={formState.isSubmitting}
            >
              Mark for deletion
            </Button>
          </ModalFooter>
        </FormProvider>
      </form>
    </ModalSimple>
  );
});

export const showMarkForDeletionModal = () => show(MarkForDeletionModal);
